import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { app } from 'store';
import axiosInstanceQueries from 'store/queries/queriesAxios';

type DownloadResponse = {
  downloadUrl: string;
};

const pageMessageSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: 8,
  textAlign: 'center',
};

function getDownloadErrorMessage(status?: number): string {
  if (status === 404) return 'This audit export could not be found.';
  if (status === 410) return 'This audit export has expired.';
  if (status === 401 || status === 403) return 'You are not authorized to download this audit export.';
  return 'There was an error preparing your audit export download. Please try again.';
}

export const AuditExportDownloadPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [downloadStarted, setDownloadStarted] = useState(false);

  useEffect(() => {
    if (!id) {
      const message = 'This audit export link is invalid.';
      setErrorMessage(message);
      dispatch(app.actions.setSnackMessage({ message, type: 'error' }));
      return;
    }

    let cancelled = false;

    async function prepareDownload() {
      try {
        const response = await axiosInstanceQueries.get<DownloadResponse>(`/audit/exports/${id}/download`, {
          params: { response: 'json' },
        });

        if (!cancelled) {
          setDownloadStarted(true);
          window.location.assign(response.data.downloadUrl);
        }
      } catch (error) {
        if (cancelled) return;
        const status = (error as { response?: { status?: number } })?.response?.status;
        const message = getDownloadErrorMessage(status);
        setErrorMessage(message);
        dispatch(app.actions.setSnackMessage({ message, type: 'error' }));
      }
    }

    prepareDownload();

    return () => {
      cancelled = true;
    };
  }, [dispatch, id]);

  if (errorMessage) {
    return (
      <Box sx={pageMessageSx}>
        <Typography variant="h5">Audit export download unavailable</Typography>
        <Typography variant="subtitle1" sx={{ marginTop: 1 }}>
          {errorMessage}
        </Typography>
      </Box>
    );
  }

  if (downloadStarted) {
    return (
      <Box sx={pageMessageSx}>
        <Typography variant="h5">Download complete</Typography>
        <Typography variant="subtitle1" sx={{ marginTop: 1 }}>
          You can now close this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={pageMessageSx}>
      <Typography variant="h5">Preparing your download...</Typography>
      <Typography variant="subtitle1" sx={{ marginTop: 1 }}>
        This may take a few seconds.
      </Typography>
      <CircularProgress size={40} sx={{ marginTop: 3 }} />
    </Box>
  );
};
